import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";

// --- PURE WEB SDK IMPORTS ---
import { auth, functions } from '@/lib/firebase';
import { signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';

function OtpBoxes({ value, onChange, disabled }: { value: string; onChange: (val: string) => void; disabled: boolean; }) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const getDigits = (): string[] => {
    const arr: string[] = [];
    for (let i = 0; i < 6; i++) arr.push(value[i] || "");
    return arr;
  };
  const digits = getDigits();

  const focusBox = (i: number) => {
    if (i >= 0 && i < 6) setTimeout(() => inputRefs.current[i]?.focus(), 0);
  };

  const buildValue = (arr: string[]) => arr.join("").replace(/[^0-9]/g, "");

  const handleChange = (i: number, raw: string) => {
    const cleaned = raw.replace(/\D/g, "");
    if (!cleaned) {
      const arr = getDigits();
      arr[i] = "";
      onChange(buildValue(arr));
      return;
    }
    if (cleaned.length > 1) {
      const full = cleaned.slice(0, 6);
      onChange(full);
      focusBox(Math.min(full.length, 5));
      return;
    }
    const arr = getDigits();
    arr[i] = cleaned[0];
    onChange(buildValue(arr));
    if (i < 5) focusBox(i + 1);
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const arr = getDigits();
      if (arr[i]) {
        arr[i] = "";
        onChange(buildValue(arr));
      } else if (i > 0) {
        arr[i - 1] = "";
        onChange(buildValue(arr));
        focusBox(i - 1);
      }
    } else if (e.key === "ArrowLeft") {
      focusBox(i - 1);
    } else if (e.key === "ArrowRight") {
      focusBox(i + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted) {
      onChange(pasted);
      focusBox(Math.min(pasted.length, 5));
    }
  };

  return (
    <div className="flex gap-2 justify-center">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { inputRefs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          value={d}
          disabled={disabled}
          className="w-11 h-12 text-center text-lg font-semibold border-2 border-border rounded-lg bg-background text-foreground focus:border-primary focus:outline-none transition-colors"
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          autoComplete={i === 0 ? "one-time-code" : "off"}
        />
      ))}
    </div>
  );
}

const AndroidLandingPage: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'PHONE' | 'OTP'>('PHONE');
  const [isLoading, setIsLoading] = useState(false);
  const [resendUntil, setResendUntil] = useState(0);
  const [resendTimer, setResendTimer] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  const hasAutoSubmitted = useRef(false);

  // --- SILENT REFERRAL GRABBER ---
  // This snatches the referral code from clipboard immediately on app open
  useEffect(() => {
    const snatchReferralCode = async () => {
      try {
        const { Clipboard } = await import('@capacitor/clipboard');
        const { value } = await Clipboard.read();

        if (value) {
          const clean = value.trim().toUpperCase();
          // Regex for exactly 8 alphanumeric characters (DailyHisaab standard)
          if (/^[A-Z0-9]{8}$/.test(clean)) {
            localStorage.setItem('early_referral_grab', clean);
            console.log("Referral code snatched and saved to local memory!");
          }
        }
      } catch (e) {
        console.warn("Clipboard access not available yet.");
      }
    };

    if (step === 'PHONE') {
      snatchReferralCode();
    }
  }, [step]);

  // THE INTERRUPTION SHIELD: Recover state if WebView was suspended
  useEffect(() => {
    const savedSession = localStorage.getItem('dailyhisaab_pending_auth');
    if (savedSession) {
      const { phone, expiresAt, resendUntil: savedResendUntil } = JSON.parse(savedSession);
      if (Date.now() < expiresAt) {
        setPhoneNumber(phone);
        setStep('OTP');
        if (savedResendUntil && Date.now() < savedResendUntil) {
          setResendUntil(savedResendUntil);
        }
      } else {
        localStorage.removeItem('dailyhisaab_pending_auth');
      }
    }
  }, []);

  // Timestamp-based Resend Timer — wall-clock driven, survives background/resume
  useEffect(() => {
    if (step !== 'OTP' || resendUntil === 0) {
      setResendTimer(0);
      return;
    }
    const recompute = () => {
      const remaining = Math.max(0, Math.ceil((resendUntil - Date.now()) / 1000));
      setResendTimer(remaining);
      return remaining;
    };
    recompute();
    const interval = setInterval(() => {
      if (recompute() === 0) clearInterval(interval);
    }, 1000);
    const onVisible = () => recompute();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [resendUntil, step]);

  const handleSendOtp = async (isResend = false) => {
    if (!phoneNumber || phoneNumber.length < 10) {
      setErrorMsg('Please enter a valid 10-digit mobile number.');
      return;
    }

    setIsLoading(true);
    setErrorMsg('');

    try {
      const formattedNumber = `91${phoneNumber}`;

      const requestOTP = httpsCallable(functions, 'requestWhatsAppOTP');
      await requestOTP({ phoneNumber: formattedNumber });

      const otpExpiresAt = Date.now() + 3 * 60 * 1000;
      const resendDeadline = Date.now() + 180 * 1000;
      localStorage.setItem('dailyhisaab_pending_auth', JSON.stringify({
        phone: phoneNumber,
        expiresAt: otpExpiresAt,
        resendUntil: resendDeadline,
      }));

      setStep('OTP');
      setResendUntil(resendDeadline);
      hasAutoSubmitted.current = false;
      if (isResend) setOtp('');

    } catch (error: any) {
      console.error('WhatsApp OTP Send Error:', error);
      setErrorMsg(error.message || 'Failed to send WhatsApp OTP.');
    } finally {
      setIsLoading(false);
    }
  };

  const doVerify = useCallback(async (code: string) => {
    if (code.length !== 6) return;

    setIsLoading(true);
    setErrorMsg('');

    try {
      const formattedNumber = `91${phoneNumber}`;

      const verifyOTP = httpsCallable(functions, 'verifyWhatsAppOTP');
      const result = await verifyOTP({ phoneNumber: formattedNumber, otp: code });

      const { customToken } = result.data as { customToken: string };

      await signInWithCustomToken(auth, customToken);

      // Clear the interruption shield upon successful login
      localStorage.removeItem('dailyhisaab_pending_auth');

    } catch (error: any) {
      console.error('OTP Verification Error:', error);
      setErrorMsg(error.message || "Invalid OTP. Please try again.");
      setOtp("");
      hasAutoSubmitted.current = false;
    } finally {
      setIsLoading(false);
    }
  }, [phoneNumber]);

  const handleOtpChange = useCallback((val: string) => {
    setOtp(val);
    setErrorMsg("");
    if (val.length === 6 && !hasAutoSubmitted.current) {
      hasAutoSubmitted.current = true;
      doVerify(val);
    }
  }, [doVerify]);

  return (
    <div className="min-h-screen flex flex-col bg-primary">
      {/* Header Visual */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-primary relative flex flex-col items-center justify-center px-6 pt-12 pb-20"
        style={{ minHeight: "40vh" }}
      >
        <div className="scale-125">
          <Logo size="lg" variant="light" />
        </div>
        <p className="text-primary-foreground/80 text-sm mt-3 tracking-wide">
          Simple Billing for Indian Businesses
        </p>
      </motion.div>

      {/* Main Login Card */}
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="flex-1 bg-[#FFFDF6] rounded-t-[2.5rem] -mt-8 relative z-10 px-6 pt-8 pb-6 shadow-2xl"
      >
        <div className="w-full max-w-sm mx-auto space-y-4">
          <p className="text-center text-[17px] font-medium text-gray-900 mb-2">
            {step === 'PHONE' ? 'Log in or sign up' : 'Enter verification code'}
          </p>

          {errorMsg && (
            <div className="w-full p-3 text-sm text-red-700 bg-red-100 rounded-lg text-center font-medium">
              {errorMsg}
            </div>
          )}

          <div className="space-y-4 mt-4">
            {step === 'PHONE' ? (
              <>
                {/* Phone Number Input Row */}
                <div className="flex w-full shadow-sm rounded-lg overflow-hidden border border-gray-300 focus-within:ring-1 focus-within:ring-[#9C1C1C] focus-within:border-[#9C1C1C]">
                  <div className="flex items-center justify-center px-4 bg-[#F5F5F5] border-r border-gray-300">
                    <span className="text-lg mr-2">🇮🇳</span>
                    <span className="text-gray-600 font-medium">+91</span>
                  </div>
                  <input
                    type="tel"
                    maxLength={10}
                    placeholder="Enter mobile number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    className="flex-1 p-3.5 outline-none text-lg tracking-wide text-gray-900 bg-white"
                    disabled={isLoading}
                  />
                </div>

                <button
                  onClick={() => handleSendOtp(false)}
                  disabled={isLoading || phoneNumber.length < 10}
                  className="w-full py-3.5 text-base font-semibold text-white bg-[#9C1C1C] rounded-lg hover:bg-[#851818] disabled:opacity-70 transition-colors shadow-md flex justify-center items-center mt-2"
                >
                  {isLoading ? <Loader2 className="h-5 v-5 animate-spin" /> : "Get WhatsApp OTP"}
                </button>
              </>
            ) : (
              <>
                <div className="flex flex-col items-center justify-center gap-1 text-sm text-gray-500 mb-6">
                  <p>Sent to WhatsApp +91 {phoneNumber}</p>
                </div>

                <OtpBoxes value={otp} onChange={handleOtpChange} disabled={isLoading} />

                {otp.length === 6 && !isLoading && (
                  <button
                    onClick={() => doVerify(otp)}
                    disabled={isLoading}
                    className="w-full py-3.5 mt-2 text-base font-semibold text-white bg-[#9C1C1C] rounded-lg hover:bg-[#851818] transition-colors shadow-md"
                  >
                    Verify & Continue
                  </button>
                )}

                {isLoading && (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Verifying...</span>
                  </div>
                )}

                {!isLoading && (
                  <div className="flex flex-col items-center mt-6 space-y-4">
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-500">Didn't receive the code?</span>
                      <button
                        onClick={() => handleSendOtp(true)}
                        disabled={resendTimer > 0 || isLoading}
                        className="text-sm font-semibold text-[#9C1C1C] disabled:text-gray-400 transition-colors"
                      >
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                      </button>
                    </div>

                    <button
                      onClick={() => {
                        setStep('PHONE');
                        setOtp("");
                        setErrorMsg("");
                        hasAutoSubmitted.current = false;
                        localStorage.removeItem('dailyhisaab_pending_auth');
                      }}
                      className="text-sm text-gray-500 underline hover:text-gray-800 transition-colors"
                    >
                      Change Mobile Number
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <p className="text-[13px] text-gray-500 text-center leading-relaxed mt-8 px-2">
            By continuing, you agree to our{" "}
            <a href="https://dailyhisaab.com/terms" target="_blank" rel="noopener noreferrer" className="text-[#9C1C1C] font-medium hover:underline">
              Terms of Service
            </a>
            {" & "}
            <a href="https://dailyhisaab.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#9C1C1C] font-medium hover:underline">
              Privacy Policy
            </a>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default AndroidLandingPage;